import { RequestRepository } from './request-repository'
import { catchError, from, map, mergeMap, of } from 'rxjs'
import * as Apollo from 'apollo-angular'
import { OperationVariables } from '@apollo/client'
import { MutationOptionsAlone } from 'apollo-angular'
import { DataAction, Payload } from '@angular-ru/ngxs/decorators'

export abstract class RequestRepositoryManaged<
  Query,
  Body extends OperationVariables,
  Extends extends { payload?: Body } & object = object
> extends RequestRepository<Query, Extends> {
  public gqlQuery: Apollo.Mutation<Query, Body>

  protected constructor(query: Apollo.Mutation<Query, Body>) {
    super()
    this.gqlQuery = query
  }

  /**
   * Will set the payload
   */
  @DataAction()
  public setPayload(@Payload('payload') payload: Body): void {
    const state = this.getState()
    this.setState({
      ...state,
      payload,
    })
  }

  /**
   * This method will fully manage a query
   * @param body
   * @param additionalInstructions
   * @param context
   * @param additionalInstructionsOnError
   */
  public query(
    body: Body,
    additionalInstructions?: (body: Body, response: Query) => Promise<any>,
    context?: MutationOptionsAlone<any>,
    additionalInstructionsOnError?: (body: Body) => Promise<any>
  ) {
    this.setLoading(true)
    this.setError(null)
    this.setPayload(body)
    return this.gqlQuery.mutate(body, context).pipe(
      mergeMap((res) => {
        this.setLoading(false)
        this.setData(res.data as Query)
        const next$ = additionalInstructions
          ? from(additionalInstructions(body, (res as any).data as Query))
          : of(undefined)
        return next$.pipe(map(() => res.data))
      }),
      catchError((error) => {
        this.setLoading(false)
        this.setError(error)
        const onErr$ = additionalInstructionsOnError
          ? from(additionalInstructionsOnError(body))
          : of(undefined)
        return onErr$.pipe(map(() => error))
      })
    )
  }

  public reload(
    additionalInstructions?: (body: Body, response: Query) => Promise<any>,
    context?: MutationOptionsAlone<any>,
    additionalInstructionsOnError?: (body: Body) => Promise<any>
  ) {
    const payload = this.getState().payload as Body
    const clone = { ...payload }
    if (payload) {
      return this.query(
        clone,
        additionalInstructions,
        context,
        additionalInstructionsOnError
      ).subscribe()
    }
    return undefined
  }
}
